'use client'

import { ArrowLeft, Briefcase, Check, Key, Sparkles, Star, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useB2bFormMutation } from '@/api/b2b/b2b-form-mutation'
import { Button } from '@/components/button'
import { Input } from '@/components/input/input'
import { homeSizeStyles } from '@/features/home/utils/home-size'
import { cn } from '@/utils/styles/cn'

const planKeys = ['plan1', 'plan2'] as const
const stepKeys = ['step1', 'step2', 'step3'] as const
const stepIcons = [Briefcase, Key, Sparkles]

type PlanKey = (typeof planKeys)[number]

const BenefitFormModal = ({ planKey, onClose }: { planKey: PlanKey; onClose: () => void }) => {
    const t = useTranslations('for-companies.benefit')
    const tm = useTranslations('for-companies.benefit.modal')
    const [companyName, setCompanyName] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [employeeCount, setEmployeeCount] = useState('')
    const [address, setAddress] = useState('')
    const [zip, setZip] = useState('')
    const [city, setCity] = useState('')
    const [country, setCountry] = useState('')
    const [message, setMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const { mutateAsync: submitB2bForm } = useB2bFormMutation()

    const planName = t(`plans.${planKey}.name`)
    const planPrice = t(`plans.${planKey}.price`)

    const disabled = useMemo(() => {
        return (
            !companyName.trim() ||
            !firstName.trim() ||
            !lastName.trim() ||
            !email.trim() ||
            !phone.trim() ||
            !employeeCount.trim() ||
            !address.trim() ||
            !zip.trim() ||
            !city.trim() ||
            !country.trim()
        )
    }, [companyName, firstName, lastName, email, phone, employeeCount, address, zip, city, country])

    const onSubmit = useCallback(async () => {
        if (disabled) return
        setIsSubmitting(true)
        try {
            await submitB2bForm({
                body: {
                    type: 'partner-onboarding',
                    companyName,
                    firstName,
                    lastName,
                    email,
                    employeeCount: Number(employeeCount),
                    package: planKey === 'plan2' ? '2-years' : '1-year',
                    phone,
                    message: message || undefined,
                    contact: {
                        firstname: firstName,
                        lastname: lastName,
                        email,
                        phone,
                    },
                    company: {
                        name: companyName,
                        address,
                        zip,
                        city,
                        country,
                    },
                },
            })
            setIsSubmitted(true)
        } finally {
            setIsSubmitting(false)
        }
    }, [
        disabled,
        companyName,
        firstName,
        lastName,
        email,
        phone,
        employeeCount,
        address,
        zip,
        city,
        country,
        message,
        planKey,
        submitB2bForm,
    ])

    const onFormKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
                onSubmit()
            }
        },
        [onSubmit]
    )

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', handleEsc)
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', handleEsc)
            document.body.style.overflow = ''
        }
    }, [onClose])

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onClose()
                }}
                role="button"
                tabIndex={0}
                aria-label="Close modal"
            />

            {/* Modal */}
            <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-l border border-gray-200 bg-white p-8 shadow-xl">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
                >
                    <X className="size-5" />
                </button>

                {isSubmitted ? (
                    <div className="flex flex-col items-center py-8">
                        <div className="mb-4 flex size-14 items-center justify-center rounded-l bg-[#2DD282]/15">
                            <Sparkles className="size-6 text-[#2DD282]" />
                        </div>
                        <h3 className="mb-2 text-2xl font-bold text-gray-900">
                            {tm('success.title')}
                        </h3>
                        <p className="text-center text-gray-500">{tm('success.description')}</p>
                    </div>
                ) : (
                    <>
                        <h2 className="mb-1 text-xl font-bold text-gray-900">
                            {tm('title', { planName, planPrice })}
                        </h2>
                        <p className="mb-6 text-sm text-gray-500">{tm('subtitle')}</p>

                        <div onKeyDown={onFormKeyDown} className="flex flex-col gap-5">
                            <Input
                                label={tm('companyName.label')}
                                value={companyName}
                                onValueChange={setCompanyName}
                                styleType="grey"
                                inputClassName="min-h-12 placeholder:text-[14px]"
                                placeholder={tm('companyName.placeholder')}
                                labeledContainerClassName="w-full"
                            />

                            <div className="grid grid-cols-2 gap-4">
                                <Input
                                    autoComplete="given-name"
                                    label={tm('firstName.label')}
                                    value={firstName}
                                    onValueChange={setFirstName}
                                    styleType="grey"
                                    inputClassName="min-h-12 placeholder:text-[14px]"
                                    placeholder={tm('firstName.placeholder')}
                                    labeledContainerClassName="w-full"
                                />
                                <Input
                                    autoComplete="family-name"
                                    label={tm('lastName.label')}
                                    value={lastName}
                                    onValueChange={setLastName}
                                    styleType="grey"
                                    inputClassName="min-h-12 placeholder:text-[14px]"
                                    placeholder={tm('lastName.placeholder')}
                                    labeledContainerClassName="w-full"
                                />
                            </div>

                            <Input
                                autoComplete="email"
                                label={tm('email.label')}
                                value={email}
                                onValueChange={setEmail}
                                styleType="grey"
                                inputClassName="min-h-12 placeholder:text-[14px]"
                                placeholder={tm('email.placeholder')}
                                labeledContainerClassName="w-full"
                            />

                            <Input
                                autoComplete="tel"
                                label={tm('phone.label')}
                                value={phone}
                                onValueChange={setPhone}
                                styleType="grey"
                                inputClassName="min-h-12 placeholder:text-[14px]"
                                placeholder={tm('phone.placeholder')}
                                labeledContainerClassName="w-full"
                            />

                            <Input
                                label={tm('employeeCount.label')}
                                value={employeeCount}
                                onValueChange={setEmployeeCount}
                                styleType="grey"
                                inputClassName="min-h-12 placeholder:text-[14px]"
                                placeholder={tm('employeeCount.placeholder')}
                                labeledContainerClassName="w-full"
                            />

                            <Input
                                autoComplete="street-address"
                                label={tm('address.label')}
                                value={address}
                                onValueChange={setAddress}
                                styleType="grey"
                                inputClassName="min-h-12 placeholder:text-[14px]"
                                placeholder={tm('address.placeholder')}
                                labeledContainerClassName="w-full"
                            />

                            <div className="grid grid-cols-2 gap-4">
                                <Input
                                    autoComplete="postal-code"
                                    label={tm('zip.label')}
                                    value={zip}
                                    onValueChange={setZip}
                                    styleType="grey"
                                    inputClassName="min-h-12 placeholder:text-[14px]"
                                    placeholder={tm('zip.placeholder')}
                                    labeledContainerClassName="w-full"
                                />
                                <Input
                                    autoComplete="address-level2"
                                    label={tm('city.label')}
                                    value={city}
                                    onValueChange={setCity}
                                    styleType="grey"
                                    inputClassName="min-h-12 placeholder:text-[14px]"
                                    placeholder={tm('city.placeholder')}
                                    labeledContainerClassName="w-full"
                                />
                            </div>

                            <Input
                                autoComplete="country-name"
                                label={tm('country.label')}
                                value={country}
                                onValueChange={setCountry}
                                styleType="grey"
                                inputClassName="min-h-12 placeholder:text-[14px]"
                                placeholder={tm('country.placeholder')}
                                labeledContainerClassName="w-full"
                            />

                            <div className="flex w-full flex-col gap-1.5">
                                <label className="text-sm font-medium text-gray-900">
                                    {tm('message.label')}
                                    <textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder={tm('message.placeholder')}
                                        rows={3}
                                        className="min-h-[80px] w-full resize-y rounded-m bg-gray-100 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-hidden focus:ring-2 focus:ring-[#2DD282]/30"
                                    />
                                </label>
                            </div>

                            <Button
                                disabled={disabled}
                                onClick={onSubmit}
                                isLoading={isSubmitting}
                                setIsLoading={setIsSubmitting}
                                styleType="primary"
                                shape="pill"
                                height="54px"
                                width="100%"
                            >
                                {tm('submitButton')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export const B2bBenefitPage = () => {
    const t = useTranslations('for-companies.benefit')
    const [modalPlanKey, setModalPlanKey] = useState<PlanKey | null>(null)

    return (
        <div className={cn('mx-auto flex w-full flex-col items-center', homeSizeStyles)}>
            <div className="flex w-full flex-col items-center px-4 py-8 md:px-0 md:py-16">
                {/* Back link */}
                <div className="mb-12 w-full max-w-[768px]">
                    <Button
                        href={{ pattern: '/for-companies', params: undefined }}
                        styleType="unstyled"
                        shape="pill"
                    >
                        <span className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900">
                            <ArrowLeft className="size-4" />
                            {t('backLink')}
                        </span>
                    </Button>
                </div>

                {/* Logo + Title */}
                <div className="mb-12 flex flex-col items-center">
                    <div className="mb-6 flex size-16 items-center justify-center rounded-l bg-gray-900">
                        <span className="text-2xl font-extrabold text-white">N</span>
                    </div>

                    <h1 className="mb-1 text-center text-4xl font-extrabold text-gray-900 md:text-5xl">
                        Neotaste
                    </h1>

                    <p className="mb-4 text-center text-lg font-semibold text-[#2DD282]">
                        {t('subtitle')}
                    </p>

                    <p className="max-w-lg text-center text-gray-500">{t('description')}</p>
                </div>

                {/* Pricing Cards */}
                <div className="mb-16 grid w-full max-w-[768px] grid-cols-1 gap-6 md:grid-cols-2">
                    {planKeys.map((planKey) => {
                        const isRecommended = planKey === 'plan2'
                        const features = t.raw(`plans.${planKey}.features`) as string[]
                        const subtitleHighlight = isRecommended
                            ? t(`plans.${planKey}.subtitleHighlight`)
                            : undefined

                        return (
                            <div
                                key={planKey}
                                className="relative flex flex-col rounded-l border border-gray-200 bg-white p-8 shadow-xs"
                            >
                                {isRecommended && (
                                    <div className="absolute right-6 top-6">
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white">
                                            <Star className="size-3" fill="currentColor" />
                                            {t('recommended')}
                                        </span>
                                    </div>
                                )}

                                <h3 className="mb-4 text-xl font-bold text-gray-900">
                                    {t(`plans.${planKey}.name`)}
                                </h3>

                                <div className="mb-2 flex items-baseline gap-1">
                                    <span className="text-5xl font-extrabold text-[#2DD282]">
                                        {t(`plans.${planKey}.price`)}€
                                    </span>
                                    <span className="text-sm text-gray-400">
                                        {t(`plans.${planKey}.period`)}
                                    </span>
                                </div>

                                <p className="mb-6 text-sm text-gray-400">
                                    {t(`plans.${planKey}.subtitle`)}
                                    {subtitleHighlight && (
                                        <span className="font-semibold text-gray-900">
                                            {' '}
                                            {subtitleHighlight}
                                        </span>
                                    )}
                                </p>

                                <ul className="mb-8 flex flex-col gap-3">
                                    {features.map((feature) => (
                                        <li key={feature} className="flex items-start gap-3">
                                            <Check
                                                className="mt-0.5 size-4 shrink-0 text-[#2DD282]"
                                                strokeWidth={3}
                                            />
                                            <span className="text-sm font-semibold text-gray-900">
                                                {feature}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto">
                                    <button
                                        type="button"
                                        onClick={() => setModalPlanKey(planKey)}
                                        className={cn(
                                            'w-full rounded-m py-3.5 text-center text-base font-semibold transition-colors',
                                            isRecommended
                                                ? 'bg-[#2DD282] text-white hover:bg-[#25b871]'
                                                : 'border-2 border-gray-200 text-gray-900 hover:border-gray-300'
                                        )}
                                    >
                                        {t('startNow')}
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* How it works */}
                <div className="flex w-full max-w-[768px] flex-col items-center">
                    <h2 className="mb-8 text-center text-3xl font-extrabold text-gray-900 md:text-4xl">
                        {t('howItWorks')}
                    </h2>

                    <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
                        {stepKeys.map((key, index) => {
                            const Icon = stepIcons[index]
                            return (
                                <div
                                    key={key}
                                    className="flex flex-col items-center rounded-l border border-gray-200 bg-white p-6 text-center shadow-xs"
                                >
                                    <span className="mb-3 text-sm font-bold tracking-widest text-gray-400">
                                        {String(index + 1).padStart(2, '0')}
                                    </span>

                                    <div className="mb-4 flex size-12 items-center justify-center rounded-m bg-[#2DD282]/10">
                                        <Icon className="size-5 text-[#2DD282]" />
                                    </div>

                                    <h3 className="mb-2 text-lg font-bold text-gray-900">
                                        {t(`steps.${key}.title`)}
                                    </h3>

                                    <p className="text-sm text-gray-500">
                                        {t(`steps.${key}.description`)}
                                    </p>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            {/* Modal */}
            {modalPlanKey && (
                <BenefitFormModal planKey={modalPlanKey} onClose={() => setModalPlanKey(null)} />
            )}
        </div>
    )
}
